import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from '../../core/service/session.service';

@Component({
  selector: 'app-session-warning-dialog',
  standalone: true,
  imports: [CommonModule],
  styleUrls: ['./session-warning-dialog.component.css'],
  templateUrl: './session-warning-dialog.component.html'
})
export class SessionWarningDialogComponent implements OnInit, OnDestroy {
  @Input() show: any;
  @Output() continueSession = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  public isDarkTheme = false;
  private mediaQuery?: MediaQueryList;
  private mutationObserver?: MutationObserver;

  constructor(
    public sessionService: SessionService,
    private readonly router: Router
  ) { }

  ngOnInit() {
    this.detectTheme();
    this.setupThemeListener();
  }

  ngOnDestroy() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleThemeChange);
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    // Remove storage event listener
    window.removeEventListener('storage', this.handleStorageChange);
  }

  private detectTheme() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Check for saved theme preference in localStorage with correct key
    const savedTheme = localStorage.getItem('user-theme-preference');
    

    if (savedTheme) {
      this.isDarkTheme = savedTheme === 'dark';
     
      return;
    }

    // Check for dark mode class on html/body
    const htmlElement = document.documentElement;
    const bodyElement = document.body;

    const hasDarkClass = htmlElement.classList.contains('dark') ||
      bodyElement.classList.contains('dark') ||
      htmlElement.classList.contains('dark-theme') ||
      bodyElement.classList.contains('dark-theme');

    if (hasDarkClass) {
      this.isDarkTheme = true;
    
      return;
    }

    // Fallback to system preference
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.isDarkTheme = systemPrefersDark;
  
  }

  private setupThemeListener() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Listen for system theme changes
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQuery.addEventListener('change', this.handleThemeChange);

    // Listen for localStorage changes (theme preference updates)
    window.addEventListener('storage', this.handleStorageChange);

    // Listen for theme changes on document
    this.mutationObserver = new MutationObserver(() => {
      this.detectTheme();
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    this.mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private readonly handleThemeChange = (e: MediaQueryListEvent) => {
    // Only update if no saved preference exists
    if (!localStorage.getItem('user-theme-preference')) {
      this.isDarkTheme = e.matches;
    }
  }

  private readonly handleStorageChange = (e: StorageEvent) => {
    // React to localStorage changes for theme preference
    if (e.key === 'user-theme-preference') {
      this.detectTheme();
    }
  }

  getSessionTimeInMinutes(): number {
    const status = this.sessionService.getSessionStatus();
    return Math.round(status.sessionTime / 60);
  }

  /**
   * Get session progress percentage (0-100)
   */
  getSessionProgressPercentage(): number {
    const status = this.sessionService.getSessionStatus();

    // Calculate progress manually as fallback
    const manualProgress = status.sessionTime > 0 ?
      Math.round((status.elapsedTime / status.sessionTime) * 100) : 0;

    const serviceProgress = status.progressPercentage || 0;
    const finalPercentage = Math.min(100, Math.max(0, serviceProgress || manualProgress));

    return finalPercentage;
  }

  /**
   * Get progress bar width style with !important to force application
   */
  getProgressWidthStyle(): string {
    const percentage = this.getSessionProgressPercentage();
    return `width: ${percentage}% !important; transition: width 0.5s ease-out !important;`;
  }

  /**
   * Get the current theme for debugging
   */
  getCurrentTheme(): string {
    return this.isDarkTheme ? 'dark' : 'light';
  }

  /**
   * Handle advanced settings click - Navigate to users profile page
   */
  openAdvancedSettings(event: Event): void {
    event.preventDefault();
    // Pause the timer to prevent logout while navigating
    this.sessionService.pauseTimer();
    this.router.navigate(['/users/profile'], { queryParams: { activeTab: 'Login Expiration Time' } });
    this.sessionService.showWarning.next(false);
  }
}

